'use client';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { YouTubeVideo } from '@/types/custom';
import { RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface YouTubeVideoSliderProps {
    videos: YouTubeVideo[];
}

interface VideoThumbnailProps {
    videoId: string;
    title: string;
}

function VideoThumbnail({ videoId, title }: VideoThumbnailProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showRotationHint, setShowRotationHint] = useState(false);

    const handlePlay = () => {
        setIsModalOpen(true);
    };

    // Detect orientation and show hint on mobile portrait
    useEffect(() => {
        const checkOrientation = () => {
            const portrait = window.innerHeight > window.innerWidth;
            const isMobile = window.innerWidth < 640; // sm breakpoint

            // Show rotation hint for mobile portrait users after opening video
            if (isModalOpen && portrait && isMobile) {
                setShowRotationHint(true);
                // Auto-hide hint after 4 seconds
                const timer = setTimeout(() => setShowRotationHint(false), 4000);
                return () => clearTimeout(timer);
            }
            setShowRotationHint(false);
        };

        checkOrientation();
        window.addEventListener('resize', checkOrientation);
        window.addEventListener('orientationchange', checkOrientation);

        return () => {
            window.removeEventListener('resize', checkOrientation);
            window.removeEventListener('orientationchange', checkOrientation);
        };
    }, [isModalOpen]);

    return (
        <>
            <button
                type="button"
                className="group relative w-full cursor-pointer"
                onClick={handlePlay}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePlay();
                    }
                }}
                aria-label={`Play video: ${title}`}
            >
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-haug-choco-70">
                    <img
                        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                        alt={title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                            // Fallback to standard quality thumbnail if maxres fails
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }}
                    />

                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 transition-opacity duration-300 group-hover:bg-opacity-40">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 transition-transform duration-300 group-hover:scale-110">
                            <svg
                                className="h-6 w-6 fill-white"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </button>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="flex w-[95vw] max-w-6xl flex-col p-0 sm:h-[95vh] sm:max-h-[800px]">
                    <DialogHeader className="sr-only">
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>
                    <div className="relative aspect-video w-full sm:flex-1">
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="h-full w-full rounded-lg"
                        />

                        {/* Rotation hint overlay for mobile portrait */}
                        {showRotationHint && (
                            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60 font-neue-haas">
                                <div className="max-w-xs p-6 text-center text-white">
                                    <RotateCcw className="mx-auto mb-3 h-8 w-8 animate-pulse" />
                                    <p className="font-neue-haas text-sm">
                                        Rotate your device for a better viewing experience
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowRotationHint(false)}
                                        className="mt-3 text-white text-xs underline opacity-75 hover:opacity-100"
                                    >
                                        Continue anyway
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Close button - always visible, higher z-index to appear above iframe */}
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-2 right-2 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-black bg-opacity-80 text-white shadow-lg transition-all duration-200 hover:bg-opacity-90"
                            aria-label="Close video"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default function YouTubeVideoSlider({ videos }: YouTubeVideoSliderProps) {
    if (!videos || videos.length === 0) {
        return null;
    }

    const isSingleVideo = videos.length === 1;

    return (
        <section className="w-full bg-haug-choco-100 py-12" aria-label="YouTube Videos">
            <div className="mx-auto max-w-screen-2xl overflow-hidden px-4 md:overflow-visible">
                {isSingleVideo ? (
                    // Single video layout - centered and larger
                    <div className="flex justify-center">
                        <div className="w-full max-w-md bg-haug-ivory-100 p-2 sm:max-w-lg md:max-w-xl lg:max-w-2xl">
                            {videos.map((video) => {
                                if (!video.id) {
                                    return null;
                                }

                                return (
                                    <div key={video.id}>
                                        <VideoThumbnail videoId={video.id} title={video.title} />
                                        {video.title && (
                                            <div className="mt-4 px-2">
                                                <h3 className="font-neue-haas font-semibold text-haug-choco-100 text-lg">
                                                    {video.title}
                                                </h3>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    // Multiple videos layout - carousel
                    <Carousel
                        opts={{
                            align: 'start',
                            loop: false,
                            dragFree: true,
                            containScroll: 'trimSnaps',
                        }}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-2 md:-ml-4">
                            {videos.map((video) => {
                                if (!video.id) {
                                    return null;
                                }

                                return (
                                    <CarouselItem
                                        key={video.id}
                                        className="basis-[85%] pl-2 sm:basis-[60%] md:basis-[45%] md:pl-4 lg:basis-[35%] xl:basis-[30%]"
                                    >
                                        <div className="relative h-full bg-haug-ivory-100 p-2">
                                            <VideoThumbnail
                                                videoId={video.id}
                                                title={video.title}
                                            />

                                            {video.title && (
                                                <div className="mt-4 px-2">
                                                    <h3 className="font-neue-haas font-semibold text-haug-choco-100 text-lg">
                                                        {video.title}
                                                    </h3>
                                                </div>
                                            )}
                                        </div>
                                    </CarouselItem>
                                );
                            })}
                        </CarouselContent>
                        <CarouselPrevious
                            size="lg"
                            className="-left-6 hidden h-12 w-12 items-center justify-center border-haug-lila-200 bg-haug-lila-100 px-3.5 text-white hover:bg-haug-lila-200 md:flex"
                        />
                        <CarouselNext
                            size="lg"
                            className="-right-6 hidden h-12 w-12 items-center justify-center border-haug-lila-200 bg-haug-lila-100 px-3.5 text-white hover:bg-haug-lila-200 md:flex"
                        />
                    </Carousel>
                )}
            </div>
        </section>
    );
}
